import type { ReactNode } from "react";
import {
  Container,
  type ContourVariant,
  Section,
  SectionHeading,
} from "@/components/landing/primitives";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { POHODA_ERROR_MESSAGES } from "@/lib/exports/pohodaErrors";

/**
 * The step-by-step body of /navod-pohoda (/en/pohoda-guide): the two ways a month gets
 * from Zettl into POHODA, with annotated screenshots of every Zettl step
 * (scripts/ui-harness/pohoda-guide-shots.sh), the mServer connection diagram and the
 * troubleshooting table.
 *
 * Zettl facts match the code (pohoda-send, pohoda.ts, pohodaErrors.ts). POHODA facts
 * come only from Stormware's public documentation, listed as the guide's sources; no
 * screenshots of POHODA itself. Prerendered: nothing reads window during render.
 */

type Tr = ReturnType<typeof makeTr>;

const SHOTS = {
  receipts: { file: "0-receipts", w: 1600, h: 931 },
  settings: { file: "1-settings", w: 1600, h: 480 },
  export: { file: "2-export", w: 1600, h: 566 },
  connection: { file: "3-connection", w: 1600, h: 1104 },
  send: { file: "4-send", w: 1600, h: 1018 },
} as const;

function Shot({
  locale,
  shot,
  alt,
}: {
  locale: LandingLocale;
  shot: keyof typeof SHOTS;
  alt: string;
}) {
  const s = SHOTS[shot];
  return (
    <img
      src={`/guides/pohoda/${locale}-${s.file}.webp`}
      width={s.w}
      height={s.h}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="mt-4 h-auto w-full rounded-xl border bg-card"
    />
  );
}

/** One numbered step: number, title, text, optional screenshot or extra content. */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[2rem_1fr] gap-x-4">
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-lg bg-primary/15 font-mono text-sm font-semibold text-[hsl(var(--action))] dark:text-primary"
      >
        {n}
      </span>
      <div className="min-w-0">
        <h4 className="text-base font-semibold leading-8">{title}</h4>
        <div className="mt-1 space-y-2 text-[15px] leading-relaxed text-muted-foreground">
          {children}
        </div>
      </div>
    </li>
  );
}

/** A menu path in POHODA, written the way its manual writes it. */
function Menu({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-secondary px-1.5 py-0.5 font-medium text-foreground [box-decoration-break:clone] [overflow-wrap:anywhere]">
      {children}
    </span>
  );
}

function SubHeading({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h3 id={id} className="mt-12 text-xl font-semibold tracking-tight first:mt-0">
      {children}
    </h3>
  );
}

function ZipTree({ tr }: { tr: Tr }) {
  const rows: [string, string][] = [
    [
      "prijate-doklady.xml",
      tr(
        "přijaté doklady měsíce (agenda Přijaté faktury)",
        "the month's received documents (Received invoices agenda)"
      ),
    ],
    ["banka.xml", tr("pohyby na účtu (agenda Banka)", "account movements (Bank agenda)")],
    ["doklady/", tr("soubory dokladů (PDF, obrázky)", "the document files (PDF, images)")],
  ];
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border bg-card p-4 font-mono text-[13px]">
      <p className="text-foreground">pohoda-&lt;firma&gt;-&lt;RRRR-MM&gt;.zip</p>
      <ul className="mt-1 space-y-1">
        {rows.map(([name, what]) => (
          <li key={name} className="flex flex-wrap gap-x-3 pl-4">
            <span className="text-foreground">{name}</span>
            <span className="font-sans text-muted-foreground">{what}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Zettl (cloud) → HTTPS → mServer → the accounting unit's database. Two drawings of
 * the same thing: across on wide screens, down on phones, so the text stays readable.
 */
function ConnectionDiagram({ tr }: { tr: Tr }) {
  const boxes = [
    {
      title: tr("Zettl (cloud)", "Zettl (cloud)"),
      lines: [
        tr("sestaví XML za měsíc", "builds the month's XML"),
        tr("ověří, že adresa je veřejná", "checks the address is public"),
        tr("uloží výsledek ke každému dokladu", "stores the result per document"),
      ],
    },
    {
      title: tr("POHODA mServer", "POHODA mServer"),
      lines: [
        tr("na vašem serveru nebo PC", "on your server or PC"),
        tr("ověří uživatele a heslo", "checks user and password"),
        tr("naimportuje a odpoví", "imports and answers"),
      ],
    },
    {
      title: tr("Účetní jednotka", "Accounting unit"),
      lines: [
        tr("databáze POHODY", "POHODA's database"),
        tr("Přijaté faktury", "Received invoices"),
        tr("Banka", "Bank"),
      ],
    },
  ];
  const arrows = ["HTTPS"];
  const label = tr(
    "Schéma: Zettl v cloudu posílá XML přes HTTPS na POHODA mServer u vás, ten ho uloží do databáze účetní jednotky.",
    "Diagram: Zettl in the cloud sends XML over HTTPS to POHODA mServer on your side, which stores it in the accounting unit's database."
  );
  const box = "fill-card stroke-border";
  const titleCls = "fill-foreground text-[15px] font-semibold";
  const lineCls = "fill-muted-foreground text-[12.5px]";
  const arrowCls = "stroke-[hsl(var(--action))] dark:stroke-primary";
  const arrowText = "fill-[hsl(var(--action))] text-[12px] font-medium dark:fill-primary";
  const sides = [
    tr("Zettl", "Zettl"),
    tr("vaše strana (účetní / firma)", "your side (accountant / company)"),
  ];

  return (
    <figure className="mt-6">
      {/* Wide: left to right */}
      <svg
        viewBox="0 0 900 250"
        role="img"
        aria-label={label}
        className="hidden h-auto w-full sm:block"
      >
        <defs>
          <marker
            id="ph-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" className="fill-[hsl(var(--action))] dark:fill-primary" />
          </marker>
        </defs>
        <rect
          x="300"
          y="8"
          width="592"
          height="234"
          rx="14"
          className="fill-secondary/40 stroke-border"
          strokeDasharray="4 4"
        />
        <text x="316" y="30" className="fill-muted-foreground text-[12px]">
          {sides[1]}
        </text>
        <text x="16" y="30" className="fill-muted-foreground text-[12px]">
          {sides[0]}
        </text>
        {boxes.map((b, i) => {
          const x = [16, 330, 620][i];
          return (
            <g key={b.title}>
              <rect x={x} y="48" width="260" height="150" rx="12" className={box} />
              <text x={x + 18} y="80" className={titleCls}>
                {b.title}
              </text>
              {b.lines.map((l, j) => (
                <text key={l} x={x + 18} y={110 + j * 24} className={lineCls}>
                  {l}
                </text>
              ))}
            </g>
          );
        })}
        <line
          x1="278"
          y1="123"
          x2="328"
          y2="123"
          className={arrowCls}
          strokeWidth="2"
          markerEnd="url(#ph-arrow)"
          markerStart="url(#ph-arrow)"
        />
        <text x="303" y="112" textAnchor="middle" className={arrowText}>
          {arrows[0]}
        </text>
        <line
          x1="592"
          y1="123"
          x2="618"
          y2="123"
          className={arrowCls}
          strokeWidth="2"
          markerEnd="url(#ph-arrow)"
        />
      </svg>

      {/* Phone: top to bottom */}
      <svg viewBox="0 0 340 586" role="img" aria-label={label} className="h-auto w-full sm:hidden">
        <defs>
          <marker
            id="ph-arrow-v"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" className="fill-[hsl(var(--action))] dark:fill-primary" />
          </marker>
        </defs>
        <rect
          x="4"
          y="196"
          width="332"
          height="386"
          rx="14"
          className="fill-secondary/40 stroke-border"
          strokeDasharray="4 4"
        />
        <text x="18" y="568" className="fill-muted-foreground text-[12px]">
          {sides[1]}
        </text>
        {boxes.map((b, i) => {
          const y = [8, 232, 404][i];
          return (
            <g key={b.title}>
              <rect x="20" y={y} width="300" height="132" rx="12" className={box} />
              <text x="38" y={y + 30} className={titleCls}>
                {b.title}
              </text>
              {b.lines.map((l, j) => (
                <text key={l} x="38" y={y + 58 + j * 22} className={lineCls}>
                  {l}
                </text>
              ))}
            </g>
          );
        })}
        <line
          x1="170"
          y1="142"
          x2="170"
          y2="228"
          className={arrowCls}
          strokeWidth="2"
          markerEnd="url(#ph-arrow-v)"
          markerStart="url(#ph-arrow-v)"
        />
        <text x="182" y="190" className={arrowText}>
          {arrows[0]}
        </text>
        <line
          x1="170"
          y1="366"
          x2="170"
          y2="400"
          className={arrowCls}
          strokeWidth="2"
          markerEnd="url(#ph-arrow-v)"
        />
      </svg>
      <figcaption className="mt-3 text-sm text-muted-foreground">
        {tr(
          "Zettl se k mServeru připojuje z internetu. Proto musí být mServer dostupný na veřejné adrese přes HTTPS – na adresu ve vnitřní síti (192.168.…, 10.…, localhost) Zettl volat nebude.",
          "Zettl connects to mServer from the internet. So mServer must be reachable on a public address over HTTPS – Zettl will not call an internal-network address (192.168.…, 10.…, localhost)."
        )}
      </figcaption>
    </figure>
  );
}

/** Error code from pohoda-send → cause and what to do. The message column is the app's own text. */
const TROUBLE: { code: string; cause: [string, string]; fix: [string, string] }[] = [
  {
    code: "mserver_unreachable",
    cause: [
      "Zettl se na adresu nedostal: mServer neběží, port není z internetu dostupný nebo spojení blokuje firewall.",
      "Zettl could not reach the address: mServer isn't running, the port isn't reachable from the internet, or a firewall blocks it.",
    ],
    fix: [
      "Spusťte mServer v POHODĚ, ověřte přesměrování portu a firewall. Když to nejde, použijte ZIP.",
      "Start mServer in POHODA, check the port forwarding and firewall. If that's not possible, use the ZIP.",
    ],
  },
  {
    code: "mserver_timeout",
    cause: [
      "mServer neodpověděl do 25 sekund (POHODA je zaneprázdněná nebo zpracovává jiný požadavek).",
      "mServer did not answer within 25 seconds (POHODA is busy or processing another request).",
    ],
    fix: [
      "Odešlete znovu. Co už POHODA má, Zettl znovu neposílá.",
      "Send again. Zettl does not resend what POHODA already has.",
    ],
  },
  {
    code: "timeout",
    cause: [
      "mServer odpověděl, že požadavek nestihl zpracovat (HTTP 408).",
      "mServer answered that it could not process the request in time (HTTP 408).",
    ],
    fix: [
      "Odešlete znovu, případně v méně vytížené době.",
      "Send again, ideally at a quieter time.",
    ],
  },
  {
    code: "auth",
    cause: [
      "POHODA uživatele nezná nebo heslo nesedí (HTTP 401).",
      "POHODA doesn't know the user or the password is wrong (HTTP 401).",
    ],
    fix: [
      "Zkontrolujte jméno uživatele v POHODĚ a heslo uložte v Zettlu znovu.",
      "Check the user name in POHODA and save the password in Zettl again.",
    ],
  },
  {
    code: "forbidden",
    cause: [
      "Uživatel nemá právo otevřít účetní jednotku nebo používat XML komunikaci (HTTP 403).",
      "The user may not open the accounting unit or use XML communication (HTTP 403).",
    ],
    fix: [
      "V agendě Přístupová práva povolte větve Práva/Soubor/Datová komunikace a Práva/Administrátorské funkce/POHODA mServer.",
      "In the Access rights agenda, allow the branches Rights/File/Data communication and Rights/Administrator functions/POHODA mServer.",
    ],
  },
  {
    code: "not_found",
    cause: [
      "Na adrese mServer neběží (HTTP 404) – nejčastěji jiný port, nebo adresa s cestou.",
      "No mServer at this address (HTTP 404) – usually a different port, or an address with a path.",
    ],
    fix: [
      "Zadejte jen https://server:port, bez /xml a bez další cesty.",
      "Enter just https://server:port, without /xml or any other path.",
    ],
  },
  {
    code: "not_mserver",
    cause: [
      "Na adrese odpovídá jiný server než POHODA mServer.",
      "Something other than POHODA mServer answers at this address.",
    ],
    fix: ["Zkontrolujte port v nastavení mServeru.", "Check the port in mServer's settings."],
  },
  {
    code: "redirect",
    cause: ["Server přesměrovává na jinou adresu.", "The server redirects to another address."],
    fix: [
      "Zadejte cílovou adresu přímo (https, správný port).",
      "Enter the final address directly (https, the right port).",
    ],
  },
  {
    code: "url_not_https",
    cause: ["Adresa začíná http://.", "The address starts with http://."],
    fix: [
      "Zapněte HTTPS v nastavení mServeru (záložka HTTPS) a zadejte adresu s https://.",
      "Switch on HTTPS in mServer's settings (HTTPS tab) and enter the address with https://.",
    ],
  },
  {
    code: "url_blocked_host",
    cause: [
      "Adresa vede do vnitřní sítě (localhost, 192.168.…, 10.…, název bez tečky). Zettl běží v cloudu a takovou adresu nevidí.",
      "The address points into an internal network (localhost, 192.168.…, 10.…, a name without a dot). Zettl runs in the cloud and cannot see it.",
    ],
    fix: [
      "Použijte veřejnou adresu (veřejná IP nebo doména s přesměrovaným portem), nebo ZIP.",
      "Use a public address (a public IP or a domain with a forwarded port), or the ZIP.",
    ],
  },
  {
    code: "url_not_resolvable",
    cause: ["Název serveru v DNS neexistuje.", "The server name does not exist in DNS."],
    fix: [
      "Zkontrolujte překlep a DNS záznam domény.",
      "Check for a typo and the domain's DNS record.",
    ],
  },
  {
    code: "no_ico",
    cause: [
      "Chybí IČO účetní jednotky. POHODA přijme data jen pro jednotku se stejným IČO.",
      "The accounting unit's IČO is missing. POHODA only accepts data for the unit with the same IČO.",
    ],
    fix: [
      "Doplňte IČO v Nastavení → Organizace, nebo v Napojení na POHODU.",
      "Add the IČO under Settings → Organization, or in the POHODA connection.",
    ],
  },
  {
    code: "admin_only",
    cause: [
      "Napojení mění jen vlastník nebo administrátor firmy v Zettlu.",
      "Only the company's owner or an admin in Zettl can change the connection.",
    ],
    fix: ["Požádejte vlastníka firmy.", "Ask the company's owner."],
  },
  {
    code: "rate_limited",
    cause: [
      "Příliš mnoho pokusů za hodinu (limit 60).",
      "Too many attempts in an hour (limit 60).",
    ],
    fix: ["Počkejte a zkuste to později.", "Wait and try again later."],
  },
];

/**
 * Three bands: the ZIP path, the mServer path and troubleshooting. They
 * alternate starting from `startTone`, so the guide template can keep its
 * bands alternating whatever comes before them.
 */
export function PohodaHowTo({
  locale,
  startTone = "plain",
  contours = [],
}: {
  locale: LandingLocale;
  startTone?: "plain" | "tinted";
  /** The contour variant of each of the three bands (the plain ones carry one). */
  contours?: readonly (ContourVariant | undefined)[];
}) {
  const other = startTone === "plain" ? "tinted" : "plain";
  const tr = makeTr(locale);
  const pick = (pair: [string, string]) => (locale === "cs" ? pair[0] : pair[1]);
  const message = (code: string) => {
    const m = POHODA_ERROR_MESSAGES[code];
    return m ? (locale === "cs" ? m.cs : m.en) : code;
  };

  return (
    <>
      <Section id="cesta-zip" labelledBy="zip-title" tone={startTone} contours={contours[0]}>
        <Container>
          <SectionHeading
            id="zip-title"
            eyebrow={tr("Cesta 1", "Way 1")}
            title={tr(
              "ZIP z Zettlu → XML import v POHODĚ",
              "ZIP from Zettl → XML import in POHODA"
            )}
            lead={tr(
              "Funguje s každou POHODOU, kde smíte používat XML import. Nic se nenastavuje, stačí stáhnout soubor a naimportovat ho.",
              "Works with any POHODA where you may use the XML import. Nothing to set up: download the file and import it."
            )}
          />
          <div className="mt-10 max-w-3xl">
            <SubHeading>{tr("V Zettlu", "In Zettl")}</SubHeading>
            <ol className="mt-6 space-y-10">
              <Step
                n={1}
                title={tr("Otevřete Nastavení → Účetnictví", "Open Settings → Accounting")}
              >
                <p>
                  {tr(
                    "Všechno k POHODĚ je na jednom místě. Na stejnou stránku vede i Účtenky → Export → Export do POHODY.",
                    "Everything about POHODA is in one place. Receipts → Export → Export to POHODA leads to the same page."
                  )}
                </p>
                <Shot
                  locale={locale}
                  shot="settings"
                  alt={tr(
                    "Nastavení Zettlu se zvýrazněnou záložkou Účetnictví",
                    "Zettl settings with the Accounting tab highlighted"
                  )}
                />
                <Shot
                  locale={locale}
                  shot="receipts"
                  alt={tr(
                    "Dialog Export na stránce Účtenky s tlačítkem Export do POHODY",
                    "The Export dialog on the Receipts page with the Export to POHODA button"
                  )}
                />
              </Step>
              <Step
                n={2}
                title={tr("Vyberte měsíc a stáhněte ZIP", "Pick the month and download the ZIP")}
              >
                <p>
                  {tr(
                    "① měsíc, ② volitelně zkratka bankovního účtu, pod kterou ho máte v POHODĚ (jinak POHODA použije výchozí účet), ③ Stáhnout pro POHODU (ZIP). Firma musí mít v Nastavení → Organizace vyplněné IČO.",
                    "① the month, ② optionally the abbreviation of the bank account in POHODA (otherwise POHODA uses its default account), ③ Download for POHODA (ZIP). The company needs its IČO under Settings → Organization."
                  )}
                </p>
                <Shot
                  locale={locale}
                  shot="export"
                  alt={tr(
                    "Karta Export do POHODY: 1 měsíc, 2 zkratka účtu, 3 tlačítko Stáhnout pro POHODU (ZIP)",
                    "Export to POHODA card: 1 month, 2 account abbreviation, 3 Download for POHODA (ZIP)"
                  )}
                />
              </Step>
              <Step n={3} title={tr("Co je v ZIPu", "What's in the ZIP")}>
                <p>
                  {tr(
                    "Dva XML soubory ve formátu, který POHODA importuje (verze 2, kódování Windows-1250), a složka se soubory dokladů. ZIP rozbalte a importujte z rozbalené složky – odkazy na soubory vedou do složky doklady.",
                    "Two XML files in the format POHODA imports (version 2, Windows-1250 encoding) and a folder with the document files. Unzip it and import from the extracted folder – the links to the files point into the doklady folder."
                  )}
                </p>
                <ZipTree tr={tr} />
              </Step>
            </ol>

            <SubHeading>{tr("V POHODĚ", "In POHODA")}</SubHeading>
            <ol className="mt-6 space-y-8">
              <Step
                n={4}
                title={tr("Spusťte průvodce XML zpracováním", "Start the XML processing wizard")}
              >
                <p>
                  {tr("V nabídce ", "From the menu ")}
                  <Menu>
                    {tr(
                      "Soubor / Datová komunikace / XML import/export…",
                      "Soubor / Datová komunikace / XML import/export…"
                    )}
                  </Menu>
                  {tr(
                    " (podle příručky POHODY). Zvolte zpracování jednoho souboru.",
                    " (as POHODA's manual names it; the Czech menu names are kept). Choose processing a single file."
                  )}
                </p>
              </Step>
              <Step
                n={5}
                title={tr(
                  "Naimportujte prijate-doklady.xml, potom banka.xml",
                  "Import prijate-doklady.xml, then banka.xml"
                )}
              >
                <p>
                  {tr(
                    "Do pole Vstupní složka nebo soubor (request) vyberte prijate-doklady.xml. Pole Výstupní složka nebo soubor (response) může zůstat prázdné. Dokončete průvodce a totéž zopakujte pro banka.xml.",
                    "In the field Vstupní složka nebo soubor (request) pick prijate-doklady.xml. The field Výstupní složka nebo soubor (response) can stay empty. Finish the wizard, then repeat it for banka.xml."
                  )}
                </p>
              </Step>
              <Step n={6} title={tr("Zkontrolujte výsledek", "Check the result")}>
                <p>
                  {tr("Výsledek importu ukáže agenda ", "The import result is in the agenda ")}
                  <Menu>Soubor / Datová komunikace / XML log</Menu>
                  {tr(". Doklady najdete v agendě ", ". The documents land in the agenda ")}
                  <Menu>Fakturace / Přijaté faktury</Menu>
                  {tr(", pohyby v agendě ", ", the movements in ")}
                  <Menu>Účetnictví / Banka</Menu>.
                </p>
              </Step>
              <Step n={7} title={tr("Spárujte platby s doklady", "Pair payments with documents")}>
                <p>
                  {tr("V agendě Banka povelem ", "In the Banka agenda, with ")}
                  <Menu>Záznam / Operace / Automatická likvidace…</Menu>
                  {tr(
                    ". POHODA páruje doklady, které mají vyplněný variabilní symbol; vlastní pravidla nastavíte v agendě Pravidla párování dokladů (Nastavení / Homebanking). Platba, kterou Zettl spároval s jedním dokladem, nese navíc jeho párovací symbol.",
                    ". POHODA pairs documents that carry a variable symbol; your own rules go in the Pravidla párování dokladů agenda (Nastavení / Homebanking). A payment Zettl matched to one document also carries that document's pairing symbol."
                  )}
                </p>
              </Step>
            </ol>
            <p className="mt-8 rounded-xl border bg-card p-4 text-[15px] leading-relaxed text-muted-foreground">
              {tr(
                "Názvy nabídek jsou z příručky POHODY; podle vaší verze POHODY se mohou mírně lišit. První import doporučujeme vyzkoušet na jednom měsíci.",
                "The menu names are from POHODA's manual and may differ slightly by your POHODA version. Try the first import on a single month."
              )}
            </p>
          </div>
        </Container>
      </Section>

      <Section id="cesta-mserver" labelledBy="mserver-title" tone={other} contours={contours[1]}>
        <Container>
          <SectionHeading
            id="mserver-title"
            eyebrow={tr("Cesta 2", "Way 2")}
            title={tr("Přímo do POHODY přes mServer", "Straight into POHODA via mServer")}
            lead={tr(
              "Bez stahování a importu: Zettl pošle měsíc rovnou do POHODY a u každého dokladu ukáže, jak dopadl. Potřebuje k tomu POHODA mServer dostupný z internetu.",
              "No download or import: Zettl sends the month straight into POHODA and shows how each document went. It needs POHODA mServer reachable from the internet."
            )}
          />
          <div className="mt-10 max-w-3xl">
            <SubHeading>{tr("Co je mServer", "What mServer is")}</SubHeading>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              {tr(
                "POHODA mServer je podle Stormware „HTTP server, který využívá HTTP protokol pro přenos XML dat pomocí páru požadavek-odpověď“ a je součástí programu POHODA. Běží na počítači nebo serveru, kde máte POHODU, přijme od Zettlu XML a naimportuje ho do účetní jednotky stejně jako ruční XML import.",
                "Per Stormware, POHODA mServer is an HTTP server that transfers XML data as request–response pairs, and it is part of POHODA. It runs on the computer or server where your POHODA is, accepts XML from Zettl and imports it into the accounting unit just like the manual XML import."
              )}
            </p>
            <ConnectionDiagram tr={tr} />

            <SubHeading>
              {tr("Co je potřeba na straně POHODY", "What you need on the POHODA side")}
            </SubHeading>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground">
              <li>
                {tr(
                  "POHODA, ve které můžete mServer používat – zda ho vaše řada a licence obsahuje, ověřte dle vaší verze POHODY.",
                  "A POHODA in which you may use mServer – whether your edition and licence include it, check against your POHODA version."
                )}
              </li>
              <li>
                {tr(
                  "Počítač nebo server, kde POHODA s mServerem běží ve chvíli odeslání. Stormware doporučuje serverový operační systém ve vnitřní síti a nedoporučuje nechávat mServer spuštěný nepřetržitě – stačí, když běží, když odesíláte.",
                  "A computer or server where POHODA with mServer runs when you send. Stormware recommends a server operating system in an internal network and advises against running mServer non-stop – it only needs to run while you send."
                )}
              </li>
              <li>
                {tr(
                  "Dostupnost z internetu přes HTTPS: veřejná adresa (veřejná IP adresa nebo doména) a na routeru přesměrovaný port mServeru. To obvykle nastaví správce sítě nebo IT firma. Kdo tohle nemá, použije ZIP – funguje vždy.",
                  "Reachability from the internet over HTTPS: a public address (a public IP or a domain) and mServer's port forwarded on the router. A network admin or IT company usually sets this up. Without it, use the ZIP – it always works."
                )}
              </li>
            </ul>

            <SubHeading>{tr("Nastavení v POHODĚ", "Setting it up in POHODA")}</SubHeading>
            <ol className="mt-6 space-y-8">
              <Step n={1} title={tr("Uživatel pro Zettl", "A user for Zettl")}>
                <p>
                  {tr("V agendě ", "In the agenda ")}
                  <Menu>Nastavení / Přístupová práva / Uživatelé</Menu>
                  {tr(
                    " založte uživatele (nebo použijte stávajícího) a povolte mu větve ",
                    " create a user (or use an existing one) and allow the branches "
                  )}
                  <Menu>Práva / Soubor / Datová komunikace</Menu>
                  {tr(" a ", " and ")}
                  <Menu>Práva / Administrátorské funkce / POHODA mServer</Menu>
                  {tr(
                    ". Uživatel potřebuje také přístup k účetní jednotce a právo zapisovat do agend Přijaté faktury a Banka.",
                    ". The user also needs access to the accounting unit and the right to write to the Přijaté faktury and Banka agendas."
                  )}
                </p>
              </Step>
              <Step n={2} title={tr("Konfigurace mServeru", "The mServer configuration")}>
                <p>
                  {tr("V agendě Účetní jednotky: ", "In the Účetní jednotky agenda: ")}
                  <Menu>Databáze / POHODA mServer</Menu>
                  {tr(
                    ", tlačítko Nový. Na záložce Základní zvolíte účetní jednotku a port, na záložce HTTPS zapnete HTTPS. Konfiguraci spustíte tlačítkem Spustit.",
                    ", button Nový. On the Základní tab pick the accounting unit and the port; on the HTTPS tab switch on HTTPS. Start the configuration with Spustit."
                  )}
                </p>
              </Step>
              <Step n={3} title={tr("Adresa pro Zettl", "The address for Zettl")}>
                <p>
                  {tr(
                    "Adresa má tvar https://vase-domena-nebo-ip:port – bez /xml a bez další cesty. Zettl si /xml a /status doplní sám.",
                    "The address looks like https://your-domain-or-ip:port – without /xml or any other path. Zettl adds /xml and /status itself."
                  )}
                </p>
              </Step>
            </ol>

            <SubHeading>{tr("V Zettlu", "In Zettl")}</SubHeading>
            <ol className="mt-6 space-y-10">
              <Step n={4} title={tr("Napojení na POHODU", "POHODA connection")}>
                <p>
                  {tr(
                    "Nastavení → Účetnictví → Napojení na POHODU (rozbalte). Vyplňte ① adresu mServeru, ② uživatele, ③ heslo a ④ IČO, pokud se liší od IČO firmy, ⑤ uložte a ⑥ otestujte spojení. Heslo Zettl ukládá šifrovaně a už ho nikomu nezobrazí; napojení nastavuje vlastník nebo administrátor firmy.",
                    "Settings → Accounting → POHODA connection (expand it). Fill in ① the mServer address, ② the user, ③ the password and ④ the IČO if it differs from the company's, ⑤ save and ⑥ test the connection. Zettl stores the password encrypted and never shows it again; the company's owner or an admin sets up the connection."
                  )}
                </p>
                <Shot
                  locale={locale}
                  shot="connection"
                  alt={tr(
                    "Napojení na POHODU: adresa, uživatel, heslo, IČO, Uložit a Otestovat spojení",
                    "POHODA connection: address, user, password, IČO, Save and Test connection"
                  )}
                />
                <p>
                  {tr(
                    "Test se zeptá mServeru na stav a název účetní jednotky. Když projde, uvidíte u napojení zelený stav a název firmy z POHODY.",
                    "The test asks mServer for its status and the accounting unit's name. When it passes, the connection shows a green state and the company name from POHODA."
                  )}
                </p>
              </Step>
              <Step n={5} title={tr("Odešlete měsíc", "Send the month")}>
                <p>
                  {tr(
                    "Na kartě Export do POHODY vyberte měsíc a klikněte na ① Odeslat do POHODY. Pod tlačítkem se objeví ② výsledek ke každému dokladu a pohybu:",
                    "On the Export to POHODA card pick the month and click ① Send to POHODA. Below it you see ② the result for each document and movement:"
                  )}
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    <strong className="text-foreground">{tr("V POHODĚ", "In POHODA")}</strong>
                    {tr(
                      " – naimportováno, s číslem dokladu v POHODĚ.",
                      " – imported, with its number in POHODA."
                    )}
                  </li>
                  <li>
                    <strong className="text-foreground">{tr("Už tam byl", "Already there")}</strong>
                    {tr(
                      " – POHODA ho už má (třeba z dřívějšího importu); nic se nezdvojí.",
                      " – POHODA already has it (e.g. from an earlier import); nothing is duplicated."
                    )}
                  </li>
                  <li>
                    <strong className="text-foreground">{tr("Chyba", "Error")}</strong>
                    {tr(
                      " – s textem, který vrátila POHODA (např. neexistující zkratka bankovního účtu). Po opravě odešlete znovu.",
                      " – with the text POHODA returned (e.g. a bank account abbreviation that doesn't exist). Fix it and send again."
                    )}
                  </li>
                </ul>
                <Shot
                  locale={locale}
                  shot="send"
                  alt={tr(
                    "Odeslat do POHODY a seznam výsledků se stavy V POHODĚ, Už tam byl a Chyba",
                    "Send to POHODA and the result list with the states In POHODA, Already there and Error"
                  )}
                />
                <p>
                  {tr(
                    "Při dalším odeslání téhož měsíce Zettl přeskočí vše, co už POHODA má, a pošle jen nové doklady a ty s chybou. Soubory dokladů se přes mServer nepřenášejí; jsou v ZIPu a v Zettlu.",
                    "When you send the same month again, Zettl skips everything POHODA already has and sends only new documents and the failed ones. Document files are not sent through mServer; they are in the ZIP and in Zettl."
                  )}
                </p>
              </Step>
            </ol>
          </div>
        </Container>
      </Section>

      <Section
        id="reseni-potizi"
        labelledBy="trouble-title"
        tone={startTone}
        contours={contours[2]}
      >
        <Container>
          <SectionHeading
            id="trouble-title"
            title={tr("Řešení potíží", "Troubleshooting")}
            lead={tr(
              "Hlášky, které Zettl při napojení a odesílání ukazuje, co znamenají a co s nimi.",
              "The messages Zettl shows when connecting and sending, what they mean and what to do."
            )}
          />
          <div className="mt-8 overflow-x-auto rounded-xl border bg-card">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="bg-secondary/50">
                  <th className="px-4 py-3 font-medium">
                    {tr("Hláška v Zettlu", "Message in Zettl")}
                  </th>
                  <th className="px-4 py-3 font-medium">{tr("Příčina", "Cause")}</th>
                  <th className="px-4 py-3 font-medium">{tr("Co udělat", "What to do")}</th>
                </tr>
              </thead>
              <tbody>
                {TROUBLE.map((row) => (
                  <tr key={row.code} className="border-t align-top">
                    <td className="px-4 py-3 text-foreground">{message(row.code)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{pick(row.cause)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{pick(row.fix)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>
    </>
  );
}

/** The error codes the troubleshooting table covers (tested against pohodaErrors.ts). */
export const POHODA_TROUBLE_CODES = TROUBLE.map((r) => r.code);
